/**
 * Import Products V2 - Lightweight Implementation
 *
 * Uses direct CSV-to-API payload without EditProduct/ProductVariant overhead.
 * See import.types.ts for the data structures.
 */
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { extractColorsFromSrc } from 'extract-colors';
import {
    AlertTriangle,
    CheckCircle2,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    FileSpreadsheet,
    FileUp,
    History,
    ImagePlus,
    Loader2,
    Lock,
    Package,
    UploadCloud,
    X,
    XCircle,
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import { PageContainer } from '../../../common/components/PageContainer';
import { useSettingsApi, config as globalConfig } from '../../../common/hooks/use-settings-api.hook';
import logger from '../../../utils/logger';
import { useProductsApi } from '../../../common/hooks/use-products-api.hook';
import { useBatchProcessing } from '../../../contexts/BatchProcessingContext';
import { useChunkedBatchUpload, ChunkedUploadOptions, PreparedProductData } from '../../../common/hooks/use-chunked-batch-upload.hook';
import { useVendorPlan } from '../../../common/hooks/use-vendor-plan.hook';
import { usePlansApi } from '../../../common/hooks/use-plans-api.hook';
import { exceedsRemaining } from '../../../common/utils/plan-limits';
import { ProductBatch } from '../../../common/utils/product-chunker';
import { BatchSession } from '../../../common/services/batch-session.service';
import { ProductLimitBanner } from '../../../components/ProductLimitBanner';
import {
    ImportProduct,
    parseCSVToImportProducts,
    buildFileLookupMap,
    normalizeFilename,
    extractColorsFromImage,
    buildV2Operations,
    getProductMatchedFileCount,
    getProductRequiredFileCount,
    productHasAllFiles,
    productIsUploadable,
    summarizeImport,
    getVariantMatchedFileCount,
    getVariantRequiredFileCount,
    variantHasAllFiles,
    isSurfaceFamily,
    isModel3dFamily,
} from './import.types';
import { useVendorFamilies } from '../../../common/hooks/use-vendor-families.hook';
import { FamilySelector } from '../../../modules/vendor/edit-product-v3/components/shared/FamilySelector';
import { CsvInstructions } from './components/CsvInstructions';
import { ImportPriceWarning } from './components/ImportPriceWarning';
import { productV2Api } from '../../../modules/vendor/edit-product-v3/api/productV2Api';
import { normalizeDisplayUnit } from '../../../common/utils/units';

// Constants
const IMAGES_PER_PAGE = 50; // Images per page in the viewer dialog
const IMAGES_LOAD_BATCH = 10; // Load images in batches to prevent memory issues
const MAX_PRODUCTS_PER_BATCH = 1; // Reduced to 1 to avoid queue backup during seamless generation
const MAX_BATCH_SIZE_MB = 500;
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

// Image extensions that are interchangeable (jpg can match png, etc.)
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);

// Helper to check if two extensions are compatible image formats
const areExtensionsCompatible = (ext1: string, ext2: string): boolean => {
    const e1 = ext1.toLowerCase();
    const e2 = ext2.toLowerCase();
    if (e1 === e2) return true;
    // Allow matching between different image formats
    return IMAGE_EXTENSIONS.has(e1) && IMAGE_EXTENSIONS.has(e2);
};

// Available patterns for layout selection
const AVAILABLE_PATTERNS = [
    { id: 'bricks', name: 'Bricks' },
    { id: 'herringbone', name: 'Herringbone' },
    { id: 'stackbond', name: 'Stack Bond' },
    { id: 'stackbond-2x2', name: 'Stack Bond 2x2' },
    { id: 'hexagonal', name: 'Hexagon' },
    { id: 'fishscale', name: 'Fish Scale' },
    { id: 'arabesque', name: 'Arabesque' },
    { id: 'alure', name: 'Alure' },
    { id: 'arrow', name: 'Arrow' },
    { id: 'penny', name: 'Penny Round' },
    { id: 'triangle', name: 'Triangle' },
    { id: 'flagstone', name: 'Flagstone' },
    { id: 'flagstonetight', name: 'Flagstone (tight)' },
    { id: 'wave', name: 'Wave' },
    { id: 'longhex', name: 'Long Hexagon' },
    { id: 'alternating', name: 'Alternating' },
    { id: 'chevron', name: 'Chevron' },
    { id: 'opus', name: 'Opus' },
    { id: 'cabochon', name: 'Cabochon' }
];

// Predefined colors
const PREDEFINED_COLORS = [
    { id: 'default', name: 'Default' },
    { id: 'Black', name: 'Black' },
    { id: 'White', name: 'White' },
    { id: 'Grey', name: 'Grey' },
    { id: 'Brown', name: 'Brown' },
    { id: 'Beige', name: 'Beige' },
    { id: 'Blue', name: 'Blue' },
    { id: 'Green', name: 'Green' },
    { id: 'Red', name: 'Red' },
    { id: 'Orange', name: 'Orange' }
];

/**
 * One statistic above the table. Four of these sit in a row, so the card
 * chrome lives here rather than being repeated per metric.
 */
const StatCard = ({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) => (
    <Card className="relative gap-0 p-4">
        <span className="absolute end-3 top-3 text-muted-foreground [&_svg]:size-4">{icon}</span>
        <p className="mb-2 pe-6 text-xs font-medium text-muted-foreground">{label}</p>
        <p className="font-heading text-2xl tracking-tight text-foreground">{value}</p>
    </Card>
);

/**
 * Status pill for a table row. Every state is the same shape — an outline, a
 * glyph and a word — so only the glyph and the tone change between them.
 */
const StatusPill = ({
    icon,
    label,
    tone = 'default',
}: { icon: React.ReactNode; label: React.ReactNode; tone?: 'default' | 'muted' | 'destructive' }) => (
    <span
        className={cn(
            'inline-flex items-center gap-1.5 border px-2 py-0.5 text-xs font-medium [&_svg]:size-3.5',
            tone === 'destructive' && 'border-destructive/40 text-destructive',
            tone === 'muted' && 'text-muted-foreground',
            tone === 'default' && 'text-foreground',
        )}
    >
        {icon}
        {label}
    </span>
);

export const ImportProductsV2 = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const csvFileInput = useRef<HTMLInputElement>(null);
    const imageFilesInput = useRef<HTMLInputElement>(null);
    const folderInput = useRef<HTMLInputElement>(null);
    const settingsAPI = useSettingsApi();

    // How big the catalogue already is, counted on the server. This used to read
    // the product list the catalogue page had loaded into Redux, which was empty
    // unless that page had been opened first, and capped at 1000 products.
    const productsAPI = useProductsApi();
    const [catalogCounts, setCatalogCounts] = useState<{ products: number; skus: number } | null>(null);
    useEffect(() => {
        productsAPI.getCatalogFacets({ draft: 'all' })
            .then((facets) => setCatalogCounts({ products: facets.total, skus: facets.skuCount }))
            .catch(() => setCatalogCounts({ products: 0, skus: 0 }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Plan caps (with vendor-level overrides applied via the hook)
    const { getLimit } = useVendorPlan();

    // Real import usage this billing period (current + limit) — used to block a
    // CSV that would exceed the REMAINING allowance, with an upgrade popup.
    const plansApi = usePlansApi();
    const [importUsage, setImportUsage] = useState<{ current: number; limit: number } | null>(null);
    const [upgradeDialog, setUpgradeDialog] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
    useEffect(() => {
        let active = true;
        plansApi.getMyPlan()
            .then((r: any) => { if (active) setImportUsage(r?.usage?.maxImportProducts ?? null); })
            .catch(() => {});
        return () => { active = false; };
    }, []);

    // Family selection
    const { families, loading: familiesLoading, defaultFamily } = useVendorFamilies();
    const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);

    // Auto-select if only one family available
    useEffect(() => {
        if (families.length === 1 && !selectedFamilyId) {
            setSelectedFamilyId(families[0]._id);
        } else if (families.length > 0 && !selectedFamilyId && defaultFamily) {
            setSelectedFamilyId(defaultFamily._id);
        }
    }, [families, selectedFamilyId, defaultFamily]);

    const selectedFamily = useMemo(
        () => families.find(f => f._id === selectedFamilyId) || null,
        [families, selectedFamilyId]
    );

    // Batch processing context - global state for progress panel
    const {
        batchStatus,
        setBatchStatus,
        isBatchProcessing,
        setIsBatchProcessing,
        batchComplete,
        setBatchComplete,
        isPanelVisible,
        setIsPanelVisible,
        isAborting,
        setIsAborting
    } = useBatchProcessing();

    // Chunked upload hook
    const chunkedUpload = useChunkedBatchUpload();

    // Core state - using lightweight ImportProduct
    const [importProducts, setImportProducts] = useState<ImportProduct[]>([]);
    const [images, setImages] = useState<File[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

    // UI state
    const [isLoadingCSV, setIsLoadingCSV] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadingProductId, setUploadingProductId] = useState<string | null>(null); // Track which product is uploading
    const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, message: '' });
    const [expandedProducts, setExpandedProducts] = useState<Record<number, boolean>>({});
    const [tablePage, setTablePage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Drag and drop state
    const [isDraggingCSV, setIsDraggingCSV] = useState(false);
    const [isDraggingImages, setIsDraggingImages] = useState(false);

    // Filter state
    const [filterMode, setFilterMode] = useState<'all' | 'missing'>('all');

    // Image viewer dialog state
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [imageViewerPage, setImageViewerPage] = useState(0);
    const [loadedImageCount, setLoadedImageCount] = useState(IMAGES_LOAD_BATCH);
    const activeObjectUrls = useRef<Set<string>>(new Set());

    // Product limit check state
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const [isCheckingLimit, setIsCheckingLimit] = useState(true);

    // Resume session state
    const [showResumeDialog, setShowResumeDialog] = useState(false);
    const [resumableSession, setResumableSession] = useState<BatchSession | null>(null);

    // Processing state indicators
    const [savedProductIds, setSavedProductIds] = useState<Set<string>>(new Set());
    const [processingProductIds, setProcessingProductIds] = useState<Set<string>>(new Set());

    // Track batches for chunked upload
    const [currentBatches, setCurrentBatches] = useState<ProductBatch[]>([]);

    // Cleanup function to revoke all active object URLs
    const cleanupObjectUrls = () => {
        activeObjectUrls.current.forEach(url => {
            URL.revokeObjectURL(url);
        });
        activeObjectUrls.current.clear();
    };

    // Progressive image loading - load more images in batches
    useEffect(() => {
        if (!imageViewerOpen) {
            cleanupObjectUrls();
            setLoadedImageCount(IMAGES_LOAD_BATCH);
            return;
        }

        const pageImages = images.slice(
            imageViewerPage * IMAGES_PER_PAGE,
            (imageViewerPage + 1) * IMAGES_PER_PAGE
        );
        const totalOnPage = pageImages.length;

        if (loadedImageCount >= totalOnPage) return;

        const timer = setTimeout(() => {
            setLoadedImageCount(prev => Math.min(prev + IMAGES_LOAD_BATCH, totalOnPage));
        }, 100); // Load next batch after 100ms

        return () => clearTimeout(timer);
    }, [imageViewerOpen, imageViewerPage, loadedImageCount, images]);

    // Reset loaded count and cleanup URLs when page changes
    useEffect(() => {
        cleanupObjectUrls();
        setLoadedImageCount(IMAGES_LOAD_BATCH);
    }, [imageViewerPage]);

    // Cleanup on unmount
    useEffect(() => {
        return () => cleanupObjectUrls();
    }, []);

    // Reset import data when family changes (prevent stale data from wrong family)
    const prevFamilyIdRef = useRef<string | null>(null);
    useEffect(() => {
        if (prevFamilyIdRef.current !== null && prevFamilyIdRef.current !== selectedFamilyId) {
            setImportProducts([]);
            setImages([]);
            setSelectedProductIds(new Set());
            setSavedProductIds(new Set());
            setProcessingProductIds(new Set());
            setTablePage(0);
            setFilterMode('all');
        }
        prevFamilyIdRef.current = selectedFamilyId;
    }, [selectedFamilyId]);

    // Product limit check
    useEffect(() => {
        if (!globalConfig) {
            setIsCheckingLimit(false);
            return;
        }
        // Still counting: say nothing yet rather than "within limit".
        if (!catalogCounts) return;

        const maxProducts = globalConfig.maxProducts ?? 50;

        // Count all SKUs including variants (siblings)
        const currentProductCount = catalogCounts.skus;

        if (currentProductCount >= maxProducts) {
            setIsLimitExceeded(true);
        } else {
            setIsLimitExceeded(false);
        }
        setIsCheckingLimit(false);
    }, [catalogCounts, globalConfig]);

    // Check for resumable batch session on mount
    useEffect(() => {
        const checkResumableSession = async () => {
            try {
                const session = await chunkedUpload.checkForResumableSession();
                if (session && session.pendingBatchIndices.length > 0) {
                    logger.log('[ImportV2] Found resumable session:', {
                        sessionId: session.sessionId,
                        completed: session.completedBatches.length,
                        pending: session.pendingBatchIndices.length,
                        failed: session.failedBatches.length
                    });
                    setResumableSession(session);
                    setShowResumeDialog(true);
                }
            } catch (error) {
                logger.error('[ImportV2] Error checking for resumable session:', error);
            }
        };
        checkResumableSession();
    }, []);

    // Lookup map for O(1) image matching
    const fileLookupMap = useMemo(() => {
        return buildFileLookupMap(importProducts);
    }, [importProducts]);

    // Get vendor brand from settings
    const [vendorBrand, setVendorBrand] = useState('');
    useEffect(() => {
        const loadBrand = async () => {
            try {
                const cfg = await settingsAPI.findConfig();
                setVendorBrand(cfg?.company || '');
            } catch (e) {
                logger.error('Failed to load config', e);
            }
        };
        loadBrand();
    }, []);

    // Handle CSV file selection
    const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const inputEl = event.target; // captured — event is pooled by the time parse completes

        setIsLoadingCSV(true);
        logger.log('[CSV] Parsing file:', file.name);

        Papa.parse(file, {
            header: true,
            dynamicTyping: (field) => field !== 'files',
            complete: (results) => {
                logger.log('[CSV] Parsed', results.data.length, 'rows');

                const products = parseCSVToImportProducts(
                    results.data,
                    vendorBrand,
                    selectedFamily?._id || '',
                    selectedFamily?.slug || '',
                    selectedFamily?.variantAxes || [],
                    // Sizes written without a unit are in the vendor's unit.
                    normalizeDisplayUnit(globalConfig?.unitDisplay)
                );

                // Reject the whole CSV up-front if its distinct products exceed the
                // remaining monthly import allowance — don't load it into the list.
                const importLimit = importUsage?.limit ?? getLimit('maxImportProducts');
                const importUsed = importUsage?.current ?? 0;
                if (exceedsRemaining(products.length, importLimit, importUsed)) {
                    const remaining = Math.max(0, importLimit - importUsed);
                    setUpgradeDialog({
                        open: true,
                        message: t(
                            'PAGES.IMPORT_PRODUCTS.OVER_IMPORT_CAP',
                            "This CSV has {{count}} products, but you can import {{remaining}} more this billing period ({{used}}/{{cap}} used). Upgrade your plan to import more.",
                            { count: products.length, remaining, used: importUsed, cap: importLimit },
                        ),
                    });
                    setIsLoadingCSV(false);
                    if (inputEl) inputEl.value = ''; // allow re-selecting the same file later
                    logger.log('[CSV] Rejected — exceeds import allowance', { products: products.length, importLimit, importUsed });
                    return;
                }

                setImportProducts(products);
                // Files already loaded must be matched against the new rows: matching
                // only re-runs when the file list changes, so a re-loaded CSV left
                // every row at 0 although its files were there.
                lastImagesRef.current = [];
                setImages(prev => (prev.length > 0 ? [...prev] : prev));

                // Auto-select all but the ones whose layout needs fixing first
                setSelectedProductIds(new Set(products.filter(p => !p.layoutError).map(p => p.id)));

                setIsLoadingCSV(false);
                logger.log('[CSV] Created', products.length, 'products');
            },
            error: (error) => {
                logger.error('[CSV] Parse error:', error);
                setIsLoadingCSV(false);
            }
        });
    };

    /*
     * Every way files arrive (picker, folder, drop) goes through here, so they all
     * accept the same kinds. Model files are accepted whenever the family or the
     * CSV itself asks for them — the drop path used to keep images only, so dropped
     * .glb files were discarded and the product could never be saved.
     */
    const addUploadedFiles = (files: File[], source: string) => {
        const acceptModel = isModel3dFamily(selectedFamily?.slug || '')
            || importProducts.some(p => p.variants.some(v => v.modelFile));
        const accepted = files.filter(f =>
            /\.(jpg|jpeg|png|gif|webp|bmp|tiff?)$/i.test(f.name) ||
            (acceptModel && /\.(glb|gltf)$/i.test(f.name))
        );
        if (accepted.length === 0) return;

        logger.log(`[${source}] Adding`, accepted.length, 'files');
        // Adding the same file twice (drop, then "Add More") matched it twice: 8/4 images.
        const key = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
        setImages(prev => {
            const seen = new Set(prev.map(key));
            return [...prev, ...accepted.filter(f => !seen.has(key(f)) && seen.add(key(f)))];
        });
    };

    // Handle image files selection
    const handleImagesUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        addUploadedFiles(Array.from(event.target.files || []), 'Images');
        event.target.value = '';
    };

    // Handle folder upload
    const handleFolderUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        addUploadedFiles(Array.from(event.target.files || []), 'Folder');
        event.target.value = '';
    };

    // Match images to products when images change
    // Use a ref to track the last images array to prevent re-runs
    const lastImagesRef = useRef<File[]>([]);

    useEffect(() => {
        if (images.length === 0 || importProducts.length === 0) return;

        // Only run if images actually changed (not just products)
        if (lastImagesRef.current === images) return;
        lastImagesRef.current = images;

        logger.log('[Matching] Matching', images.length, 'images to', importProducts.length, 'products');
        const startTime = performance.now();

        // Build lookup map inline to avoid dependency on fileLookupMap
        const lookupMap = buildFileLookupMap(importProducts);

        // Create a mutable copy of products to update
        const updatedProducts = importProducts.map(product => ({
            ...product,
            variants: product.variants.map(variant => ({
                ...variant,
                matchedFiles: [] as File[],
                matchedModelFile: undefined as File | undefined,
            }))
        }));

        // Match each image - try multiple variations for robust matching
        let matchedCount = 0;
        const unmatchedSamples: { name: string; normalized: string }[] = [];

        // Debug: log some sample keys from lookup map
        const sampleKeys = Array.from(lookupMap.keys()).slice(0, 5);
        logger.log('[Matching] Sample lookup keys:', sampleKeys);

        // Build a secondary normalized lookup for fuzzy matching
        // This maps normalized CSV filename -> entry for cases where direct lookup fails
        const normalizedLookupMap = new Map<string, { productIndex: number; variantIndex: number; isModelFile?: boolean }>();
        // Also build a list of all required files with their normalized versions for partial matching
        const allRequiredFiles: { original: string; normalized: string; entry: { productIndex: number; variantIndex: number; isModelFile?: boolean } }[] = [];

        lookupMap.forEach((entry, key) => {
            const normalized = normalizeFilename(key);
            if (!normalizedLookupMap.has(normalized)) {
                normalizedLookupMap.set(normalized, entry);
            }
            allRequiredFiles.push({ original: key, normalized, entry });
        });
        logger.log('[Matching] Built secondary normalized lookup with', normalizedLookupMap.size, 'keys');

        for (const file of images) {
            // Get base name, remove quotes if present, normalize Unicode
            const rawBaseName = file.name.split('/').pop() || '';
            const cleanBaseName = rawBaseName.replace(/^["']|["']$/g, '').trim();
            const nfcBaseName = cleanBaseName.normalize('NFC');
            const fullyNormalized = normalizeFilename(rawBaseName);

            // Try multiple lookup variations (in order of specificity)
            let match =
                lookupMap.get(rawBaseName) ||                              // 1. Exact original
                lookupMap.get(cleanBaseName) ||                            // 2. Without quotes
                lookupMap.get(nfcBaseName) ||                              // 3. NFC normalized
                lookupMap.get(fullyNormalized);                            // 4. Fully normalized

            // If no direct match, try the normalized-to-normalized lookup
            if (!match) {
                match = normalizedLookupMap.get(fullyNormalized);
            }

            // If still no match, try with alternative image extensions (jpg <-> png <-> jpeg)
            if (!match) {
                const nameWithoutExt = fullyNormalized.replace(/\.\w+$/, '');
                for (const altExt of IMAGE_EXTENSIONS) {
                    const altName = `${nameWithoutExt}.${altExt}`;
                    match = normalizedLookupMap.get(altName);
                    if (match) break;
                }
            }

            // DISABLED: Fuzzy matching disabled to avoid false positives
            // // If still no match, try partial matching (70% threshold) like convert-csv.js
            // if (!match) {
            //     const imgExt = fullyNormalized.split('.').pop()?.toLowerCase() || '';
            //
            //     for (const req of allRequiredFiles) {
            //         const reqExt = req.normalized.split('.').pop()?.toLowerCase() || '';
            //         // Allow matching between different image formats (jpg <-> png <-> jpeg)
            //         if (!areExtensionsCompatible(imgExt, reqExt)) continue;
            //
            //         // Check 70% inclusion threshold (compare without extension for cross-format matching)
            //         const imgWithoutExt = fullyNormalized.replace(/\.\w+$/, '');
            //         const reqWithoutExt = req.normalized.replace(/\.\w+$/, '');
            //         if (
            //             (reqWithoutExt.includes(imgWithoutExt) && imgWithoutExt.length > reqWithoutExt.length * 0.7) ||
            //             (imgWithoutExt.includes(reqWithoutExt) && reqWithoutExt.length > imgWithoutExt.length * 0.7)
            //         ) {
            //             match = req.entry;
            //             break;
            //         }
            //     }
            // }
            //
            // // If still no match, try keyword-based matching (STRICT mode)
            // // Only match if product-specific keywords match, not just generic category words
            // if (!match) {
            //     const imgKeywords = extractKeywords(cleanBaseName);
            //     const imgExt = fullyNormalized.split('.').pop()?.toLowerCase() || '';
            //
            //     // Common words to ignore in matching (too generic)
            //     const genericWords = new Set([
            //         'carrelage', 'sol', 'imitation', 'parquet', 'effet', 'bois',
            //         'exterieur', 'extérieur', 'moderne', 'cm', 'jpg', 'png', 'jpeg',
            //         'copie', 'copy', 'lames', 'pvc', 'clipser'
            //     ]);
            //
            //     // Filter out generic keywords
            //     const significantImgKeywords = imgKeywords.filter(kw => !genericWords.has(kw.toLowerCase()));
            //
            //     if (significantImgKeywords.length >= 2) {
            //         for (const req of allRequiredFiles) {
            //             const reqExt = req.normalized.split('.').pop()?.toLowerCase() || '';
            //             // Allow matching between different image formats (jpg <-> png <-> jpeg)
            //             if (!areExtensionsCompatible(imgExt, reqExt)) continue;
            //
            //             const reqKeywords = extractKeywords(req.original);
            //             const significantReqKeywords = reqKeywords.filter(kw => !genericWords.has(kw.toLowerCase()));
            //
            //             // Count matching significant keywords (product-specific words)
            //             const matches = significantImgKeywords.filter(kw =>
            //                 significantReqKeywords.some(rk => rk === kw || rk.includes(kw) || kw.includes(rk))
            //             );
            //
            //             // Require at least 80% of significant keywords to match
            //             const minRequired = Math.max(2, Math.ceil(significantImgKeywords.length * 0.8));
            //             if (matches.length >= minRequired) {
            //                 match = req.entry;
            //                 break;
            //             }
            //         }
            //     }
            // }

            if (match) {
                const product = updatedProducts[match.productIndex];
                if (product) {
                    const variant = product.variants[match.variantIndex];
                    if (variant) {
                        if (match.isModelFile) {
                            // This is a 3D model file match
                            variant.matchedModelFile = file;
                        } else {
                            variant.matchedFiles.push(file);
                        }
                        matchedCount++;
                    }
                }
            } else if (unmatchedSamples.length < 10) {
                // Debug: collect unmatched samples with their normalized versions
                unmatchedSamples.push({ name: rawBaseName, normalized: fullyNormalized });
            }
        }

        // Log unmatched samples for debugging
        if (unmatchedSamples.length > 0) {
            logger.log('[Matching] Unmatched image samples:', unmatchedSamples.map(s => `"${s.name}" -> normalized: "${s.normalized}"`));

            // Also log some lookup keys that contain similar patterns
            const sampleName = unmatchedSamples[0]?.normalized?.slice(0, 15) || '';
            if (sampleName) {
                const similarKeys = Array.from(lookupMap.keys())
                    .filter(k => normalizeFilename(k).includes(sampleName.slice(0, 10)))
                    .slice(0, 3);
                if (similarKeys.length > 0) {
                    logger.log('[Matching] Similar lookup keys found:', similarKeys);
                }
            }
        }

        setImportProducts(updatedProducts);

        const endTime = performance.now();
        logger.log(`[Matching] Matched ${matchedCount}/${images.length} images in ${(endTime - startTime).toFixed(1)}ms`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [images]);

    // Toggle product selection
    const toggleProductSelection = (productId: string) => {
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.add(productId);
            }
            return next;
        });
    };

    // Toggle all products
    const toggleAllProducts = () => {
        if (selectedProductIds.size === importProducts.length) {
            setSelectedProductIds(new Set());
        } else {
            setSelectedProductIds(new Set(importProducts.map(p => p.id)));
        }
    };

    // Toggle product expand
    const toggleProductExpand = (index: number) => {
        setExpandedProducts(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    // Drag and drop handlers
    const handleDragOver = (e: React.DragEvent, setDragging: (v: boolean) => void) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent, setDragging: (v: boolean) => void) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
    };

    const handleCSVDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingCSV(false);

        const files = Array.from(e.dataTransfer.files);
        const csvFile = files.find(f => f.name.endsWith('.csv'));
        if (csvFile) {
            // Simulate file input change
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(csvFile);
            if (csvFileInput.current) {
                csvFileInput.current.files = dataTransfer.files;
                csvFileInput.current.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }
    };

    const handleImagesDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingImages(false);

        if (importProducts.length === 0) return;

        addUploadedFiles(Array.from(e.dataTransfer.files), 'DragDrop');
    };

    // Save a SINGLE product (for per-row save button)
    const handleSaveSingleProduct = async (product: ImportProduct) => {
        if (uploadingProductId) return; // Already uploading something

        setUploadingProductId(product.id);

        try {
            // Mark as uploading
            setImportProducts(prev => prev.map(p =>
                p.id === product.id ? { ...p, status: 'uploading' as const } : p
            ));

            // Extract colors from first image
            let extractedColors = ['#907361'];
            const firstImage = product.variants.find(v => v.matchedFiles.length > 0)?.matchedFiles[0];
            if (firstImage) {
                extractedColors = await extractColorsFromImage(firstImage, extractColorsFromSrc);
            }

            // Build V2 operations payload
            const { operations, files } = buildV2Operations(product, extractedColors);

            if (operations.operations.length === 0) {
                logger.warn(`[Upload] No variants selected for ${product.name}`);
                setImportProducts(prev => prev.map(p =>
                    p.id === product.id ? { ...p, status: 'error' as const, errorMessage: 'No variants selected' } : p
                ));
                return;
            }

            // Build FormData with V2 format. Flag as import so the backend meters
            // the created product against the monthly maxImportProducts quota.
            const formData = new FormData();
            formData.append('data', JSON.stringify({ ...operations, isImport: true }));
            formData.append('source', 'import');

            // Append files - already named correctly by buildAPIPayload
            for (const { file, metaType } of files) {
                if (metaType === 'plank' || metaType === 'product_image' || metaType === 'model3d' || metaType === 'thumbnail') {
                    formData.append('images', file, file.name);
                }
            }

            // Submit via V2 bulk endpoint
            const batchResponse = await productV2Api.bulkOperations(formData);

            if (!batchResponse.batchId) {
                throw new Error('Backend did not return batchId');
            }

            // Poll for completion
            const result = await productV2Api.pollBulkStatus(batchResponse.batchId);

            // Check if any jobs failed
            const failedJobs = result?.jobs?.filter((job: any) => job.status === 'failed') || [];
            if (failedJobs.length > 0) {
                const errorMessage = failedJobs[0]?.errorDetails?.userMessage ||
                    failedJobs[0]?.error ||
                    'Upload failed';
                logger.error(`[Upload] Job failed for ${product.name}:`, errorMessage);
                setImportProducts(prev => prev.map(p =>
                    p.id === product.id ? { ...p, status: 'error' as const, errorMessage } : p
                ));
                return;
            }

            // Mark as done only if all jobs succeeded
            setImportProducts(prev => prev.map(p =>
                p.id === product.id ? { ...p, status: 'done' as const } : p
            ));

            logger.log(`[Upload] Successfully saved ${product.name}`);
        } catch (error) {
            logger.error(`[Upload] Failed for ${product.name}:`, error);
            setImportProducts(prev => prev.map(p =>
                p.id === product.id ? { ...p, status: 'error' as const, errorMessage: String(error) } : p
            ));
        } finally {
            setUploadingProductId(null);
        }
    };

    // Estimate product size in bytes (for batching)
    const estimateProductSize = useCallback((product: ImportProduct): number => {
        let totalSize = 0;
        product.variants.forEach(variant => {
            variant.matchedFiles.forEach(file => {
                totalSize += file.size;
            });
        });
        // Add overhead for JSON payload (~10KB per product)
        return totalSize + 10 * 1024;
    }, []);

    // Create batches from ImportProducts for chunked upload
    const createBatchesFromImportProducts = useCallback((products: ImportProduct[]): ProductBatch[] => {
        const batches: ProductBatch[] = [];
        let currentBatch: ImportProduct[] = [];
        let currentBatchSize = 0;

        for (const product of products) {
            const productSize = estimateProductSize(product);

            // Check if adding this product would exceed limits
            if (currentBatch.length >= MAX_PRODUCTS_PER_BATCH ||
                currentBatchSize + productSize > MAX_BATCH_SIZE_MB * 1024 * 1024) {
                // Save current batch if not empty
                if (currentBatch.length > 0) {
                    batches.push({
                        index: batches.length,
                        products: currentBatch as any, // Type compatibility
                        productIds: currentBatch.map(p => p.id),
                        plankGroupIds: [...new Set(currentBatch.map(p => p.plankGroupId))],
                        estimatedSizeMB: currentBatchSize / (1024 * 1024)
                    });
                }
                // Start new batch
                currentBatch = [];
                currentBatchSize = 0;
            }

            currentBatch.push(product);
            currentBatchSize += productSize;
        }

        // Don't forget the last batch
        if (currentBatch.length > 0) {
            batches.push({
                index: batches.length,
                products: currentBatch as any,
                productIds: currentBatch.map(p => p.id),
                plankGroupIds: [...new Set(currentBatch.map(p => p.plankGroupId))],
                estimatedSizeMB: currentBatchSize / (1024 * 1024)
            });
        }

        logger.log(`[ChunkedUpload] Created ${batches.length} batches from ${products.length} products`);
        return batches;
    }, [estimateProductSize]);

    // Build API payload for a batch of products (V2 operations format)
    const buildAPIPayloadForBatch = useCallback(async (
        batchProducts: any[] // ImportProduct[]
    ): Promise<PreparedProductData> => {
        const allOperations: any[] = [];
        const allFiles: PreparedProductData['files'] = [];

        for (const importProduct of batchProducts as ImportProduct[]) {
            // Extract colors from first matched file
            let extractedColors = ['#907361'];
            const firstFile = importProduct.variants.find(v => v.matchedFiles.length > 0)?.matchedFiles[0];
            if (firstFile) {
                try {
                    extractedColors = await extractColorsFromImage(firstFile, extractColorsFromSrc);
                } catch (e) {
                    logger.warn(`[ColorExtraction] Failed for ${importProduct.name}, using default`);
                }
            }

            const { operations, files: productFiles } = buildV2Operations(importProduct, extractedColors);
            // For batch upload, we merge operations from all products
            allOperations.push(...operations.operations);
            allFiles.push(...productFiles);
        }

        // Use the first product's family info for the batch wrapper
        const firstProduct = batchProducts[0] as ImportProduct;
        const batchOperations = {
            productGroupId: firstProduct?.id || '',
            familyId: firstProduct?.familyId || '',
            familySlug: firstProduct?.familySlug || '',
            operations: allOperations,
            // Flags this bulk request as a CSV import so the backend meters the
            // created products against the vendor's monthly maxImportProducts quota.
            isImport: true,
        };

        logger.log(`[buildAPIPayloadForBatch] Prepared ${allOperations.length} operations and ${allFiles.length} files`);
        return { operations: batchOperations, files: allFiles };
    }, []);

    // Abort entire import (all batches)
    const handleAbortBatch = useCallback(async () => {
        if (isAborting) return;

        setIsAborting(true);
        try {
            logger.log(`[ImportV2] Aborting import - stopping all batches`);

            // First, pause the chunked upload loop to prevent next batches from starting
            await chunkedUpload.pauseUpload();

            // Then abort the current batch if one is in progress
            if (batchStatus?.batchId) {
                logger.log(`[ImportV2] Aborting current batch ${batchStatus.batchId}`);
                await productV2Api.abortBulkBatch(batchStatus.batchId);
            }

            setIsBatchProcessing(false);
            setBatchComplete(true);
            logger.log(`[ImportV2] Import aborted successfully`);
        } catch (error) {
            logger.error('[ImportV2] Error aborting import:', error);
        } finally {
            setIsAborting(false);
        }
    }, [batchStatus, isAborting, chunkedUpload, setIsAborting, setIsBatchProcessing, setBatchComplete]);

    // Handle resume upload from saved session
    // Note: True resume would require reloading the same CSV/images and reconstructing batches
    // For now, we just close the dialog - the session info is kept so user knows their progress
    const handleResumeUpload = useCallback(async () => {
        if (!resumableSession) return;

        // Just close the dialog - user needs to reload same files to continue
        // The previous session's progress is informational only
        setShowResumeDialog(false);

        // Clear the old session since we can't truly resume without the original data
        try {
            await chunkedUpload.clearSession(resumableSession.sessionId);
        } catch (error) {
            logger.error('[ImportV2] Error clearing session:', error);
        }
        setResumableSession(null);

        logger.log('[ImportV2] Session cleared - user should reload CSV and images to start new import');
    }, [resumableSession, chunkedUpload]);

    // Handle starting fresh (discard saved session)
    const handleStartFresh = useCallback(async () => {
        if (resumableSession) {
            await chunkedUpload.clearSession(resumableSession.sessionId);
        }
        setShowResumeDialog(false);
        setResumableSession(null);
    }, [resumableSession, chunkedUpload]);

    // Create ALL selected products using chunked batch upload
    const handleCreateProducts = async () => {
        // Filter products that are selected, not done, and have all required files
        // Same rule as the "Upload Selected (n)" count (summarizeImport).
        const selectedProducts = importProducts.filter(p =>
            selectedProductIds.has(p.id) && productIsUploadable(p)
        );

        if (selectedProducts.length === 0) {
            logger.warn('[ChunkedUpload] No products ready for upload');
            return;
        }

        // Plan cap — block a CSV that would exceed the REMAINING monthly import
        // allowance (limit − already imported this period), with an upgrade popup.
        // The backend stays authoritative (403), this is the friendly pre-check.
        const importLimit = importUsage?.limit ?? getLimit('maxImportProducts');
        const importUsed = importUsage?.current ?? 0;
        if (exceedsRemaining(selectedProducts.length, importLimit, importUsed)) {
            const remaining = Math.max(0, importLimit - importUsed);
            setUpgradeDialog({
                open: true,
                message: t(
                    'PAGES.IMPORT_PRODUCTS.OVER_IMPORT_CAP',
                    "This import has {{count}} products, but you can import {{remaining}} more this billing period ({{used}}/{{cap}} used). Upgrade your plan to import more.",
                    { count: selectedProducts.length, remaining, used: importUsed, cap: importLimit },
                ),
            });
            return;
        }
        const maxProductsLimit = getLimit('maxProducts');
        const currentCount = catalogCounts?.products ?? 0;
        if (exceedsRemaining(selectedProducts.length, maxProductsLimit, currentCount)) {
            const remainingCapacity = Math.max(0, maxProductsLimit - currentCount);
            setUpgradeDialog({
                open: true,
                message: t(
                    'PAGES.IMPORT_PRODUCTS.OVER_TOTAL_CAP',
                    'You have {{remaining}} product slot(s) remaining on your current plan ({{current}}/{{cap}} used). Cannot import {{selected}} more. Upgrade your plan to add more products.',
                    {
                        remaining: remainingCapacity,
                        current: currentCount,
                        cap: maxProductsLimit,
                        selected: selectedProducts.length,
                    },
                ),
            });
            return;
        }

        logger.log(`[ChunkedUpload] Starting upload for ${selectedProducts.length} products`);

        // Create batches
        const batches = createBatchesFromImportProducts(selectedProducts);
        setCurrentBatches(batches);

        // Set processing state
        setIsUploading(true);
        setIsBatchProcessing(true);
        setBatchComplete(false);
        setIsPanelVisible(true);

        // Configure upload callbacks
        const uploadOptions: ChunkedUploadOptions = {
            onBatchStart: (batchIndex, totalBatches) => {
                logger.log(`[ChunkedUpload] Starting batch ${batchIndex + 1}/${totalBatches}`);
                setUploadProgress({
                    current: batchIndex,
                    total: totalBatches,
                    message: `Uploading batch ${batchIndex + 1} of ${totalBatches}...`
                });

                // Mark products in this batch as uploading
                const batch = batches[batchIndex];
                if (batch) {
                    setProcessingProductIds(prev => {
                        const newSet = new Set(prev);
                        batch.productIds.forEach(id => newSet.add(id));
                        return newSet;
                    });
                    setImportProducts(prev => prev.map(p =>
                        batch.productIds.includes(p.id) ? { ...p, status: 'uploading' as const } : p
                    ));
                }
            },

            onBatchComplete: (batchIndex, batchId) => {
                logger.log(`[ChunkedUpload] Batch ${batchIndex + 1} completed with batchId: ${batchId}`);

                // Mark products in this batch as done (but preserve error status from polling)
                const batch = batches[batchIndex];
                if (batch) {
                    setProcessingProductIds(prev => {
                        const newSet = new Set(prev);
                        batch.productIds.forEach(id => newSet.delete(id));
                        return newSet;
                    });
                    // Only mark as 'done' if not already in error state
                    // Error state is set by onPollingUpdate when individual jobs fail
                    setImportProducts(prev => prev.map(p => {
                        if (!batch.productIds.includes(p.id)) return p;
                        // Preserve error status - don't overwrite with done
                        if (p.status === 'error') return p;
                        return { ...p, status: 'done' as const };
                    }));
                    // Only add to saved set if not in error
                    setSavedProductIds(prev => {
                        const newSet = new Set(prev);
                        batch.productIds.forEach(id => {
                            // Check if product is not in error state before adding to saved
                            // Note: we can't access current state here, so we rely on onPollingUpdate
                            // to have already updated the status correctly
                            newSet.add(id);
                        });
                        return newSet;
                    });
                }
            },

            onBatchFailed: (batchIndex, error) => {
                logger.error(`[ChunkedUpload] Batch ${batchIndex + 1} failed:`, error);

                // Mark products in this batch as error
                const batch = batches[batchIndex];
                if (batch) {
                    setProcessingProductIds(prev => {
                        const newSet = new Set(prev);
                        batch.productIds.forEach(id => newSet.delete(id));
                        return newSet;
                    });
                    setImportProducts(prev => prev.map(p =>
                        batch.productIds.includes(p.id)
                            ? { ...p, status: 'error' as const, errorMessage: error }
                            : p
                    ));
                }
            },

            onProgress: (progress) => {
                setUploadProgress({
                    current: progress.completedBatches,
                    total: progress.totalBatches,
                    message: progress.status === 'polling'
                        ? `Waiting for batch ${(progress.currentBatchIndex ?? 0) + 1} to complete...`
                        : `Batch ${(progress.currentBatchIndex ?? 0) + 1} of ${progress.totalBatches}`
                });
            },

            onPollingUpdate: (batchIndex, status) => {
                logger.log('[PollingUpdate] Called with batchIndex:', batchIndex, 'status:', status);

                if (status?.summary) {
                    const { summary, jobs } = status;
                    logger.log('[PollingUpdate] Summary:', summary, 'Jobs count:', jobs?.length || 0);

                    const processed = summary.completed + summary.failed;
                    const percentage = summary.total > 0 ? Math.round((processed / summary.total) * 100) : 0;

                    // Extract errors from failed jobs
                    const errors = (jobs || [])
                        .filter((job: any) => job.status === 'failed')
                        .map((job: any) => ({
                            productId: job.productData?.id || 'unknown',
                            productName: job.productData?.name || 'Unknown',
                            errorMessage: job.errorDetails?.userMessage || job.error || 'Unknown error',
                            suggestion: job.errorDetails?.suggestion || 'Please retry',
                            retryable: job.errorDetails?.retryable ?? true,
                            field: job.errorDetails?.field,
                            currentValue: job.errorDetails?.currentValue,
                            category: job.errorDetails?.category,
                            jobId: job._id
                        }));

                    // Update individual product statuses based on job results
                    // Note: job.productData.id is the VARIANT ID (from buildAPIPayload), not the ImportProduct.id
                    // So we need to check if any variant of a product matches the job ID
                    if (jobs && jobs.length > 0) {
                        const failedVariantIds = new Set(
                            jobs.filter((job: any) => job.status === 'failed')
                                .map((job: any) => job.productData?.id)
                                .filter(Boolean)
                        );
                        const completedVariantIds = new Set(
                            jobs.filter((job: any) => job.status === 'completed')
                                .map((job: any) => job.productData?.id)
                                .filter(Boolean)
                        );

                        logger.log('[PollingUpdate] Failed variant IDs from jobs:', [...failedVariantIds]);
                        logger.log('[PollingUpdate] Completed variant IDs from jobs:', [...completedVariantIds]);

                        if (failedVariantIds.size > 0 || completedVariantIds.size > 0) {
                            // Track which products need status updates
                            const productsToMarkFailed: string[] = [];
                            const productsToMarkDone: string[] = [];

                            setImportProducts(prev => {
                                // Debug: log variant IDs from our products
                                logger.log('[PollingUpdate] Our product variant IDs:', prev.map(p => ({
                                    productId: p.id,
                                    productName: p.name,
                                    variantIds: p.variants.map(v => v.id)
                                })));

                                return prev.map(p => {
                                    // Check if any variant of this product failed
                                    const hasFailedVariant = p.variants.some(v => failedVariantIds.has(v.id));
                                    if (hasFailedVariant) {
                                        logger.log(`[PollingUpdate] Product "${p.name}" has failed variant, marking as error`);
                                        productsToMarkFailed.push(p.id);
                                        // Find the failed job to get error message
                                        const failedVariantId = p.variants.find(v => failedVariantIds.has(v.id))?.id;
                                        const failedJob = jobs.find((job: any) => job.productData?.id === failedVariantId && job.status === 'failed');
                                        return {
                                            ...p,
                                            status: 'error' as const,
                                            errorMessage: failedJob?.errorDetails?.userMessage || failedJob?.error || 'Failed'
                                        };
                                    }
                                    // Check if all selected variants of this product completed
                                    const selectedVariants = p.variants.filter(v => v.isSelected);
                                    const allCompleted = selectedVariants.length > 0 &&
                                        selectedVariants.every(v => completedVariantIds.has(v.id));
                                    if (allCompleted && p.status !== 'done') {
                                        productsToMarkDone.push(p.id);
                                        return { ...p, status: 'done' as const };
                                    }
                                    return p;
                                });
                            });

                            // Update saved/processing sets
                            if (productsToMarkDone.length > 0) {
                                setSavedProductIds(prevSaved => {
                                    const newSet = new Set(prevSaved);
                                    productsToMarkDone.forEach(id => newSet.add(id));
                                    return newSet;
                                });
                            }
                            setProcessingProductIds(prevProcessing => {
                                const newSet = new Set(prevProcessing);
                                productsToMarkFailed.forEach(id => newSet.delete(id));
                                productsToMarkDone.forEach(id => newSet.delete(id));
                                return newSet;
                            });
                        }
                    }

                    setBatchStatus({
                        batchId: status.batchId,
                        total: summary.total,
                        pending: summary.pending,
                        generating_seamless: summary.generating_seamless || 0,
                        processing: summary.processing,
                        completed: summary.completed,
                        failed: summary.failed,
                        percentage,
                        jobs: jobs || [],
                        errors
                    });
                }
            },

            productBuilder: buildAPIPayloadForBatch
        };

        try {
            // Start the chunked upload
            const result = await chunkedUpload.startBatchUpload(batches, uploadOptions);

            logger.log(`[ChunkedUpload] Upload complete:`, {
                success: result.success,
                completedBatches: result.completedBatches,
                failedBatches: result.failedBatches,
                totalBatches: result.totalBatches
            });

            setUploadProgress({
                current: result.totalBatches,
                total: result.totalBatches,
                message: result.success ? 'Complete!' : `Completed with ${result.failedBatches} failed batches`
            });
        } catch (error) {
            logger.error('[ChunkedUpload] Error during upload:', error);
            setUploadProgress({
                current: 0,
                total: batches.length,
                message: `Error: ${error}`
            });
        } finally {
            setIsUploading(false);
            setIsBatchProcessing(false);
            setBatchComplete(true);
            setProcessingProductIds(new Set());
        }
    };

    // Filtered products based on filter mode
    const filteredProducts = useMemo(() => {
        if (filterMode === 'missing') {
            return importProducts.filter(p => !productHasAllFiles(p));
        }
        return importProducts;
    }, [importProducts, filterMode]);

    // Pagination
    const paginatedProducts = useMemo(() => {
        const start = tablePage * rowsPerPage;
        return filteredProducts.slice(start, start + rowsPerPage);
    }, [filteredProducts, tablePage, rowsPerPage]);

    // Stats
    // Every count reads the rule the row badge reads (summarizeImport), so the cards,
    // the rows and the upload button cannot disagree (ARDE-270).
    const stats = useMemo(
        () => summarizeImport(importProducts, selectedProductIds),
        [importProducts, selectedProductIds],
    );

    // Tooltip on the upload CTA — empty means no tooltip at all, so it is
    // computed once here rather than rendering an empty bubble.
    const uploadHint = stats.selectedNotReady > 0
        ? t('PAGES.IMPORT_PRODUCTS.MISSING_IMAGES_WARNING', { count: stats.selectedNotReady })
        : stats.selectedReady === 0
            ? t('PAGES.IMPORT_PRODUCTS.NO_PRODUCTS_READY')
            : '';

    const uploadButton = (
        <Button
            className="h-9"
            onClick={handleCreateProducts}
            disabled={isUploading || stats.selectedReady === 0}
        >
            {isUploading
                ? `${uploadProgress.current}/${uploadProgress.total} ${uploadProgress.message}`
                : t('PAGES.IMPORT_PRODUCTS.UPLOAD_SELECTED', { count: stats.selectedReady })}
        </Button>
    );

    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / rowsPerPage));
    const firstRow = filteredProducts.length === 0 ? 0 : tablePage * rowsPerPage + 1;
    const lastRow = Math.min((tablePage + 1) * rowsPerPage, filteredProducts.length);

    return (
        <PageContainer breadcrumbs={[
            { label: t('SIDEBAR.PRODUCTS.TITLE') },
            { label: t('SIDEBAR.PRODUCTS.IMPORT_PRODUCTS'), icon: <FileUp className="size-5" /> }
        ]}>
            {/* Product Limit Banner */}
            {isLimitExceeded && (
                <ProductLimitBanner maxProducts={globalConfig?.maxProducts ?? 50} />
            )}

            {/* Family selector + History button — compact, aligned in a single
                row. Family card hugs its content on the left; History sits on
                the right. */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="flex min-w-0 flex-wrap items-center gap-4 rounded-xl border bg-card px-3 py-2">
                    <div className="min-w-0">
                        <p className="whitespace-nowrap text-xs font-medium text-foreground">
                            {t('PAGES.IMPORT_PRODUCTS.SELECT_FAMILY', 'Product Family')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                            {t('PAGES.IMPORT_PRODUCTS.SELECT_FAMILY_HINT', 'Select the product family for imported products')}
                        </p>
                    </div>
                    <FamilySelector
                        families={families}
                        selectedFamilyId={selectedFamilyId}
                        onFamilySelect={setSelectedFamilyId}
                        loading={familiesLoading}
                    />
                </div>

                <Button
                    variant="outline"
                    className="h-9"
                    onClick={() => navigate('/products/import-history')}
                >
                    <History />
                    {t('SIDEBAR.PRODUCTS.IMPORT_HISTORY')}
                </Button>
            </div>

            {/* CSV Import Guide */}
            <CsvInstructions familySlug={selectedFamily?.slug} />

            {/* Content wrapper with disabled state when limit exceeded */}
            <div className={cn('relative', isLimitExceeded && 'pointer-events-none opacity-50')}>
                {/* Blur overlay when limit exceeded */}
                {isLimitExceeded && (
                    <div className="pointer-events-auto absolute inset-0 z-10 bg-background/60 backdrop-blur-sm" />
                )}

                {/* Upload Section */}
                <div className="grid gap-6 md:grid-cols-2">
                    {/* CSV Upload */}
                    <div
                        className={cn(
                            'flex min-h-[220px] cursor-pointer flex-col items-center justify-center gap-3 border border-dashed p-6 text-center transition-colors',
                            isDraggingCSV
                                ? 'border-foreground bg-muted text-foreground'
                                : 'border-input bg-card text-muted-foreground hover:border-foreground hover:bg-muted/50',
                            !selectedFamilyId && 'pointer-events-none cursor-not-allowed opacity-50',
                        )}
                        onClick={() => csvFileInput.current?.click()}
                        onDragOver={(e) => handleDragOver(e, setIsDraggingCSV)}
                        onDragLeave={(e) => handleDragLeave(e, setIsDraggingCSV)}
                        onDrop={handleCSVDrop}
                    >
                        <FileSpreadsheet className="size-9" strokeWidth={1.25} />
                        <p className="text-sm font-medium">
                            {t('PAGES.IMPORT_PRODUCTS.UPLOAD_CSV')}
                        </p>
                        <p className="text-xs">
                            {isDraggingCSV ? t('PAGES.IMPORT_PRODUCTS.UPLOAD_CSV_DRAG') : t('PAGES.IMPORT_PRODUCTS.UPLOAD_CSV_HINT')}
                        </p>

                        <input
                            type="file"
                            onChange={handleCSVUpload}
                            accept=".csv"
                            ref={csvFileInput}
                            style={{ display: 'none' }}
                        />

                        {importProducts.length > 0 && (
                            <Badge variant="outline" className="mt-2">
                                {t('PAGES.IMPORT_PRODUCTS.CSV_LOADED', { count: importProducts.length })}
                            </Badge>
                        )}
                    </div>

                    {/* Image Upload */}
                    <div
                        className={cn(
                            'flex min-h-[220px] flex-col items-center justify-center gap-3 border border-dashed p-6 text-center transition-colors',
                            isDraggingImages
                                ? 'border-foreground bg-muted text-foreground'
                                : 'border-input bg-card text-muted-foreground',
                            importProducts.length === 0
                                ? 'pointer-events-none cursor-not-allowed opacity-50'
                                : images.length > 0
                                    ? 'cursor-default'
                                    : 'cursor-pointer hover:border-foreground hover:bg-muted/50',
                        )}
                        onClick={importProducts.length === 0 ? undefined : (images.length === 0 ? () => imageFilesInput.current?.click() : undefined)}
                        onDragOver={(e) => importProducts.length > 0 && handleDragOver(e, setIsDraggingImages)}
                        onDragLeave={(e) => importProducts.length > 0 && handleDragLeave(e, setIsDraggingImages)}
                        onDrop={(e) => importProducts.length > 0 ? handleImagesDrop(e) : e.preventDefault()}
                    >
                        {images.length === 0 ? (
                            // Show upload instructions when no images
                            <>
                                <ImagePlus className="size-9" strokeWidth={1.25} />
                                <p className="text-sm font-medium">
                                    {isModel3dFamily(selectedFamily?.slug || '')
                                        ? t('PAGES.IMPORT_PRODUCTS.UPLOAD_IMAGES_AND_MODELS', 'Upload Images & 3D Models')
                                        : t('PAGES.IMPORT_PRODUCTS.UPLOAD_IMAGES')}
                                </p>
                                <p className="text-xs">
                                    {importProducts.length === 0
                                        ? t('PAGES.IMPORT_PRODUCTS.UPLOAD_CSV_FIRST', 'Please upload a CSV file first')
                                        : isDraggingImages
                                            ? t('PAGES.IMPORT_PRODUCTS.UPLOAD_IMAGES_DRAG')
                                            : isModel3dFamily(selectedFamily?.slug || '')
                                                ? t('PAGES.IMPORT_PRODUCTS.UPLOAD_FILES_HINT', 'Drop images and .glb/.gltf model files here')
                                                : t('PAGES.IMPORT_PRODUCTS.UPLOAD_IMAGES_HINT')}
                                </p>
                            </>
                        ) : (
                            // Show image count and action buttons (no thumbnails to save memory)
                            <>
                                <Badge variant="outline" className="mb-2">
                                    {`${images.length} ${t('PAGES.IMPORT_PRODUCTS.IMAGES_LOADED', 'images loaded')}`}
                                </Badge>

                                <div className="flex flex-wrap justify-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setImageViewerPage(0);
                                            setImageViewerOpen(true);
                                        }}
                                    >
                                        {t('PAGES.IMPORT_PRODUCTS.VIEW_IMAGES', 'View Images')}
                                    </Button>

                                    <Button variant="outline" size="sm" onClick={() => imageFilesInput.current?.click()}>
                                        {t('PAGES.IMPORT_PRODUCTS.ADD_MORE', 'Add More')}
                                    </Button>

                                    <Button variant="outline" size="sm" onClick={() => folderInput.current?.click()}>
                                        {t('PAGES.IMPORT_PRODUCTS.UPLOAD_FOLDER', 'Upload Folder')}
                                    </Button>

                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setImages([])}
                                        className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                                    >
                                        {t('PAGES.IMPORT_PRODUCTS.CLEAR_ALL', 'Clear All')}
                                    </Button>
                                </div>
                            </>
                        )}

                        <input
                            type="file"
                            onChange={handleImagesUpload}
                            accept={isModel3dFamily(selectedFamily?.slug || '') || importProducts.some(p => p.variants.some(v => v.modelFile)) ? 'image/*,.glb,.gltf' : 'image/*'}
                            multiple
                            ref={imageFilesInput}
                            style={{ display: 'none' }}
                        />
                        <input
                            type="file"
                            // @ts-ignore - webkitdirectory is not in types
                            webkitdirectory=""
                            ref={folderInput}
                            style={{ display: 'none' }}
                            onChange={handleFolderUpload}
                        />
                    </div>
                </div>

                {/* Products Table */}
                {importProducts.length > 0 && (
                    <>
                        {/* Stats Cards Row */}
                        <div className="mt-6 mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
                            <StatCard label={t('PAGES.IMPORT_PRODUCTS.TOTAL_PRODUCTS')} value={stats.total} icon={<Package />} />
                            <StatCard label={t('PAGES.IMPORT_PRODUCTS.READY_TO_UPLOAD')} value={stats.ready} icon={<CheckCircle2 />} />
                            <StatCard label={t('PAGES.IMPORT_PRODUCTS.UPLOADED')} value={stats.done} icon={<UploadCloud />} />
                            <StatCard
                                label={stats.errors > 0
                                    ? t('PAGES.IMPORT_PRODUCTS.ERRORS')
                                    : t(stats.hasModelFiles ? 'PAGES.IMPORT_PRODUCTS.AWAITING_FILES' : 'PAGES.IMPORT_PRODUCTS.AWAITING_IMAGES')}
                                value={stats.errors > 0 ? stats.errors : stats.awaitingFiles}
                                icon={stats.errors > 0 ? <XCircle /> : <AlertTriangle />}
                            />
                        </div>

                        {/* Action Bar */}
                        <Card className="mb-4 flex-row flex-wrap items-center justify-between gap-4 px-4 py-3">
                            <div className="flex items-center gap-3">
                                <Checkbox
                                    checked={
                                        selectedProductIds.size === importProducts.length
                                            ? true
                                            : selectedProductIds.size > 0
                                                ? 'indeterminate'
                                                : false
                                    }
                                    onCheckedChange={toggleAllProducts}
                                    aria-label={t('PAGES.IMPORT_PRODUCTS.SELECT_ALL')}
                                />
                                <p className="text-sm text-foreground">
                                    {selectedProductIds.size === 0
                                        ? t('PAGES.IMPORT_PRODUCTS.SELECT_PRODUCTS')
                                        : selectedProductIds.size === importProducts.length
                                            ? t('PAGES.IMPORT_PRODUCTS.ALL_PRODUCTS_SELECTED', { count: importProducts.length })
                                            : t('PAGES.IMPORT_PRODUCTS.PRODUCTS_SELECTED_OF', { selected: selectedProductIds.size, total: importProducts.length })}
                                </p>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                                <Button
                                    variant="outline"
                                    className={cn('h-9', filterMode === 'missing' && 'border-foreground')}
                                    onClick={() => {
                                        setFilterMode(prev => prev === 'all' ? 'missing' : 'all');
                                        setTablePage(0); // Reset to first page when filter changes
                                    }}
                                >
                                    {filterMode === 'missing'
                                        ? t('PAGES.IMPORT_PRODUCTS.MISSING_IMAGES_COUNT', { count: filteredProducts.length })
                                        : t('PAGES.IMPORT_PRODUCTS.SHOW_MISSING_IMAGES')}
                                </Button>
                                <Button variant="outline" className="h-9" onClick={toggleAllProducts}>
                                    {selectedProductIds.size === importProducts.length ? t('PAGES.IMPORT_PRODUCTS.DESELECT_ALL') : t('PAGES.IMPORT_PRODUCTS.SELECT_ALL')}
                                </Button>
                                {uploadHint ? (
                                    <Tooltip>
                                        {/* A disabled button emits no pointer events; the span carries them. */}
                                        <TooltipTrigger asChild>
                                            <span>{uploadButton}</span>
                                        </TooltipTrigger>
                                        <TooltipContent>{uploadHint}</TooltipContent>
                                    </Tooltip>
                                ) : uploadButton}
                            </div>
                        </Card>

                        {/* Upload Progress */}
                        {isUploading && (
                            <div className="mb-4">
                                <div className="mb-1.5 flex items-center justify-between">
                                    <p className="text-xs text-muted-foreground">
                                        {t('PAGES.IMPORT_PRODUCTS.UPLOADING_PRODUCTS')}
                                    </p>
                                    <p className="text-xs font-medium text-foreground">
                                        {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                                    </p>
                                </div>
                                <Progress value={(uploadProgress.current / uploadProgress.total) * 100} className="h-2" />
                            </div>
                        )}

                        {/* Table */}
                        <div className="border">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/40">
                                        <TableHead className="w-12" />
                                        <TableHead className="w-12" />
                                        {[
                                            t('PAGES.IMPORT_PRODUCTS.PRODUCT'),
                                            t('PAGES.IMPORT_PRODUCTS.TYPE'),
                                            t('PAGES.IMPORT_PRODUCTS.VARIANTS'),
                                            t('PAGES.IMPORT_PRODUCTS.IMAGES'),
                                            t('PAGES.IMPORT_PRODUCTS.STATUS'),
                                            t('PAGES.IMPORT_PRODUCTS.ACTIONS'),
                                        ].map((header) => (
                                            <TableHead key={header} className="uppercase tracking-wider text-muted-foreground">
                                                {header}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {paginatedProducts.map((product, pageIndex) => {
                                        const index = tablePage * rowsPerPage + pageIndex;
                                        const matchedCount = getProductMatchedFileCount(product);
                                        const requiredCount = getProductRequiredFileCount(product);
                                        const hasAllFiles = productHasAllFiles(product);
                                        // Any family can name a model (a Decor rug with a .glb), not only furniture and lighting.
                                        const showModelColumn = isModel3dFamily(selectedFamily?.slug || '')
                                            || product.variants.some(v => v.modelFile);

                                        return (
                                            <React.Fragment key={product.id}>
                                                {/* Main Row */}
                                                <TableRow>
                                                    <TableCell>
                                                        <Button
                                                            variant="outline"
                                                            size="icon-sm"
                                                            aria-expanded={!!expandedProducts[index]}
                                                            aria-label={(expandedProducts[index] ? t('COMMON.COLLAPSE') : t('COMMON.EXPAND')) as string}
                                                            onClick={() => toggleProductExpand(index)}
                                                        >
                                                            <ChevronDown className={cn('transition-transform', expandedProducts[index] && 'rotate-180')} />
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Checkbox
                                                            checked={selectedProductIds.has(product.id)}
                                                            onCheckedChange={() => toggleProductSelection(product.id)}
                                                            aria-label={product.name}
                                                        />
                                                    </TableCell>
                                                    <TableCell className="text-sm font-medium text-foreground">
                                                        {product.name}
                                                        <ImportPriceWarning warnings={product.variants.flatMap((v) => v.priceWarnings ?? [])} />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline">{product.type}</Badge>
                                                    </TableCell>
                                                    <TableCell className="text-sm text-foreground">
                                                        {product.variants.length}
                                                    </TableCell>
                                                    <TableCell>
                                                        {/* Image count with mini progress indicator */}
                                                        <div className="flex items-center gap-2">
                                                            <Progress
                                                                value={requiredCount > 0 ? (matchedCount / requiredCount) * 100 : 0}
                                                                className="w-10"
                                                            />
                                                            <span className={cn('text-xs font-medium', hasAllFiles ? 'text-foreground' : 'text-muted-foreground')}>
                                                                {matchedCount}/{requiredCount}
                                                            </span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        {/* Done Status */}
                                                        {product.status === 'done' && (
                                                            <StatusPill icon={<CheckCircle2 />} label="Uploaded" />
                                                        )}

                                                        {/* Error Status - with tooltip carrying the message */}
                                                        {product.status === 'error' && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <span className="cursor-help">
                                                                        <StatusPill
                                                                            icon={<XCircle />}
                                                                            label={product.layoutError ? t('PAGES.IMPORT_PRODUCTS.INVALID_LAYOUT', 'Invalid layout') : 'Failed'}
                                                                            tone="destructive"
                                                                        />
                                                                    </span>
                                                                </TooltipTrigger>
                                                                <TooltipContent>{product.errorMessage || 'Upload failed'}</TooltipContent>
                                                            </Tooltip>
                                                        )}

                                                        {/* Uploading Status */}
                                                        {product.status === 'uploading' && (
                                                            <StatusPill icon={<Loader2 className="animate-spin" />} label="Uploading..." tone="muted" />
                                                        )}

                                                        {/* Pending Status - Ready or Waiting */}
                                                        {product.status === 'pending' && hasAllFiles && (
                                                            <StatusPill
                                                                icon={<span className="size-2 shrink-0 rounded-full bg-foreground" />}
                                                                label={t('PAGES.IMPORT_PRODUCTS.READY')}
                                                            />
                                                        )}

                                                        {product.status === 'pending' && !hasAllFiles && (
                                                            <StatusPill
                                                                tone="muted"
                                                                icon={<span className="size-2 shrink-0 animate-pulse rounded-full bg-muted-foreground" />}
                                                                label={matchedCount > 0
                                                                    ? t(product.variants.some(v => v.modelFile) ? 'PAGES.IMPORT_PRODUCTS.FILES_COUNT' : 'PAGES.IMPORT_PRODUCTS.IMAGES_COUNT', { matched: matchedCount, required: requiredCount })
                                                                    : t('PAGES.IMPORT_PRODUCTS.AWAITING_IMAGES_STATUS')}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {product.status === 'pending' && (
                                                            <Button
                                                                size="sm"
                                                                disabled={!hasAllFiles || isUploading || uploadingProductId !== null}
                                                                onClick={() => handleSaveSingleProduct(product)}
                                                            >
                                                                {uploadingProductId === product.id ? (
                                                                    <Loader2 className="animate-spin" />
                                                                ) : null}
                                                                {t('PAGES.IMPORT_PRODUCTS.SAVE')}
                                                            </Button>
                                                        )}
                                                        {product.status === 'done' && (
                                                            <Button variant="ghost" size="sm" onClick={() => navigate('/products')}>
                                                                View
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>

                                                {/* Expanded Variants */}
                                                {expandedProducts[index] && (
                                                    <TableRow className="hover:bg-transparent">
                                                        <TableCell colSpan={8} className="bg-muted/40 p-4">
                                                            <Table>
                                                                <TableHeader>
                                                                    <TableRow className="hover:bg-transparent">
                                                                        <TableHead className="text-muted-foreground">{t('PAGES.IMPORT_PRODUCTS.SKU')}</TableHead>
                                                                        {/* Dynamic axis columns from family config */}
                                                                        {(selectedFamily?.variantAxes || []).filter(a => a.enabled).map(axis => (
                                                                            <TableHead key={axis.name} className="text-muted-foreground">
                                                                                {typeof axis.label === 'string' ? axis.label : axis.label?.en || axis.name}
                                                                            </TableHead>
                                                                        ))}
                                                                        <TableHead className="text-muted-foreground">{t('PAGES.IMPORT_PRODUCTS.IMAGES')}</TableHead>
                                                                        {showModelColumn && (
                                                                            <TableHead className="text-muted-foreground">{t('PAGES.IMPORT_PRODUCTS.MODEL_3D', '3D Model')}</TableHead>
                                                                        )}
                                                                    </TableRow>
                                                                </TableHeader>
                                                                <TableBody>
                                                                    {product.variants.map(variant => (
                                                                        <TableRow key={variant.id} className="hover:bg-transparent">
                                                                            <TableCell>{variant.sku}</TableCell>
                                                                            {/* Dynamic axis value cells */}
                                                                            {(selectedFamily?.variantAxes || []).filter(a => a.enabled).map(axis => {
                                                                                const val = variant.axisValues?.[axis.name];
                                                                                let display = '-';
                                                                                if (val !== undefined && val !== null) {
                                                                                    if (axis.valueType === 'dimensions' && typeof val === 'object') {
                                                                                        display = val.depth
                                                                                            ? `${val.width}x${val.height}x${val.depth} ${val.unit || 'cm'}`
                                                                                            : `${val.width}x${val.height} ${val.unit || 'cm'}`;
                                                                                    } else {
                                                                                        display = String(val);
                                                                                    }
                                                                                }
                                                                                return (
                                                                                    <TableCell key={axis.name}>{display}</TableCell>
                                                                                );
                                                                            })}
                                                                            {/* Same count as the row badge: images plus the model the CSV names */}
                                                                            <TableCell
                                                                                className={cn(
                                                                                    variantHasAllFiles(variant)
                                                                                        ? 'text-foreground'
                                                                                        : 'text-muted-foreground',
                                                                                )}
                                                                            >
                                                                                {getVariantMatchedFileCount(variant)}/{getVariantRequiredFileCount(variant)}
                                                                            </TableCell>
                                                                            {showModelColumn && (
                                                                                <TableCell className={cn(variant.matchedModelFile ? 'text-foreground' : 'text-muted-foreground')}>
                                                                                    {!variant.modelFile
                                                                                        ? '-'
                                                                                        : variant.matchedModelFile
                                                                                            ? variant.matchedModelFile.name
                                                                                            // The CSV name alone read as if the model were already there.
                                                                                            : t('PAGES.IMPORT_PRODUCTS.MODEL_NOT_LOADED', { file: variant.modelFile })}
                                                                                </TableCell>
                                                                            )}
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>

                        {/* Pagination */}
                        <div className="mt-4 flex flex-wrap items-center justify-end gap-4 rounded-xl border bg-card px-4 py-3">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-foreground">
                                    {t('PAGES.MY_PRODUCTS.PAGINATION.ROWS_PER_PAGE')}
                                </span>
                                <Select
                                    value={String(rowsPerPage)}
                                    onValueChange={(value) => {
                                        setRowsPerPage(parseInt(value, 10));
                                        setTablePage(0);
                                    }}
                                >
                                    <SelectTrigger className="h-8 w-[72px]">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {ROWS_PER_PAGE_OPTIONS.map(option => (
                                            <SelectItem key={option} value={String(option)}>{option}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <span className="text-xs text-muted-foreground">
                                {`${firstRow}-${lastRow} ${t('PAGES.MY_PRODUCTS.PAGINATION.OF')} ${filteredProducts.length}`}
                            </span>
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    aria-label={t('PAGES.IMPORT_PRODUCTS.PREVIOUS', 'Previous')}
                                    disabled={tablePage === 0}
                                    onClick={() => setTablePage(prev => Math.max(0, prev - 1))}
                                >
                                    <ChevronLeft className="rtl:rotate-180" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="icon-sm"
                                    aria-label={t('PAGES.IMPORT_PRODUCTS.NEXT', 'Next')}
                                    disabled={tablePage >= totalPages - 1}
                                    onClick={() => setTablePage(prev => Math.min(totalPages - 1, prev + 1))}
                                >
                                    <ChevronRight className="rtl:rotate-180" />
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            {/* End of content wrapper */}

            {/* Plan-limit reached — upgrade prompt (blocks the import).
                Matches the compose-email LimitReachedGate card: lock circle, centered. */}
            <Dialog open={upgradeDialog.open} onOpenChange={(open) => !open && setUpgradeDialog({ open: false, message: '' })}>
                <DialogContent className="sm:max-w-[480px]">
                    <DialogHeader className="items-center text-center">
                        <span className="mb-2 flex size-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
                            <Lock className="size-6" />
                        </span>
                        <DialogTitle>{t('PAGES.IMPORT_PRODUCTS.UPGRADE_TITLE', 'Plan limit reached')}</DialogTitle>
                        <DialogDescription>{upgradeDialog.message}</DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="sm:justify-center">
                        <Button variant="outline" onClick={() => setUpgradeDialog({ open: false, message: '' })}>
                            {t('COMMON.CLOSE', 'Close')}
                        </Button>
                        <Button onClick={() => { setUpgradeDialog({ open: false, message: '' }); navigate('/home/billing/plan'); }}>
                            {t('PLANS.LOCKED.UPGRADE_CTA', 'View plans')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Resume Session Dialog */}
            <Dialog open={showResumeDialog} onOpenChange={(open) => !open && setShowResumeDialog(false)}>
                <DialogContent className="sm:max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Previous Import Found</DialogTitle>
                        <DialogDescription>An incomplete import session was found:</DialogDescription>
                    </DialogHeader>
                    <ul className="list-disc space-y-1 ps-5 text-xs text-muted-foreground">
                        <li>{resumableSession?.completedBatches?.length || 0} batches completed</li>
                        <li>{resumableSession?.pendingBatchIndices?.length || 0} batches remaining</li>
                        {(resumableSession?.failedBatches?.length || 0) > 0 && (
                            <li className="text-destructive">
                                {resumableSession?.failedBatches?.length} batches failed
                            </li>
                        )}
                    </ul>
                    <p className="text-xs text-muted-foreground">
                        This session will be cleared. Please reload your CSV and images to start a new import.
                    </p>
                    <DialogFooter>
                        <Button onClick={handleStartFresh}>OK</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Image Viewer Dialog with Pagination */}
            <Dialog open={imageViewerOpen} onOpenChange={(open) => !open && setImageViewerOpen(false)}>
                <DialogContent className="max-h-[90vh] gap-4 overflow-y-auto sm:max-w-4xl">
                    <DialogHeader>
                        <DialogTitle>
                            {t('PAGES.IMPORT_PRODUCTS.UPLOADED_IMAGES', 'Uploaded Images')} ({images.length})
                        </DialogTitle>
                    </DialogHeader>

                    <div className="flex flex-wrap justify-start gap-3">
                        {(() => {
                            const pageImages = images.slice(
                                imageViewerPage * IMAGES_PER_PAGE,
                                (imageViewerPage + 1) * IMAGES_PER_PAGE
                            );
                            const visibleImages = pageImages.slice(0, loadedImageCount);
                            const remainingCount = pageImages.length - loadedImageCount;

                            return (
                                <>
                                    {visibleImages.map((file, index) => {
                                        const globalIndex = imageViewerPage * IMAGES_PER_PAGE + index;
                                        const imageUrl = URL.createObjectURL(file);
                                        activeObjectUrls.current.add(imageUrl);

                                        return (
                                            <div
                                                key={`${file.name}-${globalIndex}`}
                                                className="relative size-[100px] overflow-hidden border"
                                            >
                                                <img
                                                    src={imageUrl}
                                                    alt={file.name}
                                                    className="size-full object-cover"
                                                    onLoad={() => {
                                                        URL.revokeObjectURL(imageUrl);
                                                        activeObjectUrls.current.delete(imageUrl);
                                                    }}
                                                />

                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className="absolute inset-x-0 bottom-0 block truncate bg-foreground/60 px-1 py-0.5 text-[10px] text-background">
                                                            {file.name}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent>{file.name}</TooltipContent>
                                                </Tooltip>

                                                <Button
                                                    variant="ghost"
                                                    size="icon-xs"
                                                    className="absolute end-1 top-1 bg-foreground/60 text-background hover:bg-foreground/80 hover:text-background"
                                                    aria-label={t('COMMON.REMOVE_IMAGE', 'Remove image') as string}
                                                    onClick={() => {
                                                        setImages(prev => prev.filter((_, i) => i !== globalIndex));
                                                    }}
                                                >
                                                    <X />
                                                </Button>
                                            </div>
                                        );
                                    })}

                                    {/* Loading indicator for remaining images */}
                                    {remainingCount > 0 && (
                                        <div className="flex w-full items-center justify-center gap-2 py-4">
                                            <Loader2 className="size-5 animate-spin text-muted-foreground" />
                                            <span className="text-xs text-muted-foreground">
                                                {t('PAGES.IMPORT_PRODUCTS.LOADING_MORE', 'Loading')} {remainingCount} {t('PAGES.IMPORT_PRODUCTS.MORE_IMAGES', 'more images...')}
                                            </span>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>

                    {/* Pagination info */}
                    {images.length > IMAGES_PER_PAGE && (
                        <p className="text-center text-xs text-muted-foreground">
                            {t('PAGES.IMPORT_PRODUCTS.SHOWING_IMAGES', 'Showing')} {imageViewerPage * IMAGES_PER_PAGE + 1} - {Math.min((imageViewerPage + 1) * IMAGES_PER_PAGE, images.length)} {t('PAGES.IMPORT_PRODUCTS.OF', 'of')} {images.length}
                        </p>
                    )}

                    <DialogFooter className="sm:justify-between">
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setImageViewerPage(prev => Math.max(0, prev - 1))}
                                disabled={imageViewerPage === 0}
                            >
                                {t('PAGES.IMPORT_PRODUCTS.PREVIOUS', 'Previous')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => setImageViewerPage(prev => Math.min(Math.ceil(images.length / IMAGES_PER_PAGE) - 1, prev + 1))}
                                disabled={(imageViewerPage + 1) * IMAGES_PER_PAGE >= images.length}
                            >
                                {t('PAGES.IMPORT_PRODUCTS.NEXT', 'Next')}
                            </Button>
                        </div>
                        <Button onClick={() => setImageViewerOpen(false)}>
                            {t('PAGES.IMPORT_PRODUCTS.CLOSE', 'Close')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
};
